import {
  Component,
  computed,
  ElementRef, EventEmitter,
  HostBinding,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { AccordionIconPosition, AccordionSize, AccordionType } from './accordion-interface';
import { generateId } from '../../../_core/utils/generate-id';

@Component({
  selector: 'opac-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
})
export class AccordionComponent {
  private animation: Animation | null = null;
  private isShrinking = false;
  private isExpanding = false;

  /**
   * Selects complete accordion element (details)
   */
  @ViewChild('details', { static: true }) detailsEl: ElementRef<HTMLDetailsElement>;
  /**
   * Selects accordion title (summary)
   */
  @ViewChild('summary', { static: true }) summaryEl: ElementRef<HTMLElement>;
  /**
   * Selects accordion content
   */
  @ViewChild('content', { static: true }) contentEl: ElementRef<HTMLElement>;

  get isOpen() {
    if (!this.detailsEl) {
      return false;
    }

    return this.detailsEl.nativeElement.open;
  }

  readonly id = signal(generateId());
  readonly size = signal<AccordionSize>('small');
  readonly iconName = computed(() => {
    return this.size() === 'large' ? 'navigate_downward' : 'expand_more';
  });

  /**
   * Determines the marker icon position in the title. Defaults to 'end'
   */
  @Input() iconPosition: AccordionIconPosition = 'end';

  /**
   * Determines the initial state (open/close) of the accordion. Defaults to false (closed)
   */
  @Input('open') set openInput(value: boolean) {
    if (value) {
      this.handleToggleAccordion(false);
    }
  };

  /**
   * Determines the size of the accordion based on its title. Defaults to 'small'
   */
  @Input('size') set sizeInput(value: AccordionSize) {
    this.size.set(value);
  }

  /**
   * Determines accordion title.
   */
  @Input() title: string = '';

  /**
   * Determines accordion appearance. Defaults to 'solid'
   */
  @Input() type: AccordionType = 'solid';

  /**
   * Emits when the user opens or closes the accordion
   */
  @Output() toggle = new EventEmitter<{ open: boolean }>();

  /**
   * Set host classes to handle appearance
   */
  @HostBinding('class') get classAttribute() {
    return {
      [this.size()]: true,
      [this.type]: true,
    };
  }

  toggleAccordion(event: Event) {
    event.preventDefault();

    this.handleToggleAccordion(true);
  }

  openAccordion() {
    this.detailsEl.nativeElement.style.overflow = 'hidden';
    this.detailsEl.nativeElement.style.height = `${this.detailsEl.nativeElement.offsetHeight}px`;
    this.detailsEl.nativeElement.open = true;

    requestAnimationFrame(() => {
      this.expand();
    });
  }

  closeAccordion() {
    this.detailsEl.nativeElement.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.shrink();
    });
  }

  private handleToggleAccordion(emit = false) {
    if (this.isShrinking || !this.isOpen) {
      this.openAccordion();
      if (emit) {
        this.toggle.emit({ open: true });
      }
    } else if (this.isExpanding || this.isOpen) {
      this.closeAccordion();
      if (emit) {
        this.toggle.emit({ open: false });
      }
    }
  }

  private expand() {
    this.isExpanding = true;

    const startHeight = `${this.detailsEl.nativeElement.offsetHeight}px`; // -2
    const endHeight = `${this.summaryEl.nativeElement.offsetHeight + this.contentEl.nativeElement.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.detailsEl.nativeElement.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 200,
        easing: 'ease-out',
      },
    );

    this.animation.onfinish = () => { this.onAnimationFinish(true) };
    this.animation.oncancel = () => { this.isExpanding = false; };
  }

  private shrink() {
    this.isShrinking = true;

    const startHeight = `${this.detailsEl.nativeElement.offsetHeight}px`; // -2
    const endHeight = `${this.summaryEl.nativeElement.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.detailsEl.nativeElement.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 200,
        easing: 'ease-out',
      },
    );

    this.animation.onfinish = () => { this.onAnimationFinish(false) };
    this.animation.oncancel = () => { this.isShrinking = false; };
  }

  private onAnimationFinish(isOpen: boolean) {
    this.detailsEl.nativeElement.open = isOpen;
    this.animation = null;
    this.isShrinking = false;
    this.isExpanding = false;

    this.detailsEl.nativeElement.style.height = this.detailsEl.nativeElement.style.overflow = '';
  }
}
